import { computed, ref } from "vue";
import { defineStore } from "pinia";
import {
  useCotizacionEstandarCalculos,
  useCotizacionEstandarCalculosTabla,
  useCotizacionEstandar,
} from "@/composables/useCotizacionEstandar";

export const useCotizacionEstandarStore = defineStore(
  "cotizacion-estandar-store",
  () => {
    const clientes = ref([]);
    const tablaTIR = ref([]);
    const usuarioLoggeado = ref(undefined);
    const {
      precioFinal,
      tdcInformativo,
      obtenerDatoPorPlazo,
      keyMetrics,
    } = useCotizacionEstandar();

    function formatDate(date) {
      return date.toISOString().slice(0, 10);
    }

    const today = new Date();
    const datePlus30 = new Date();
    datePlus30.setDate(today.getDate() + 30);

    const state = ref({
      arrendamiento: undefined,
      cliente_id: undefined,
      descripcion: undefined,
      monedaEquipo: "USD",
      monedaOperacion: "MXN",
      valorEquipo: 45000,
      tipoArrendamiento: "puro",
      fechaCierre: formatDate(today),
      fechaPrimerVencimiento: formatDate(datePlus30),
    });

    const stateCalculosLeaseParameters = ref({
      delq_interest: 36,
      vat: 16,
      withholdingTax: 0,
      facilityCoupon: 14,
      marginBuffer: 0,
      servicingFee: 0.5,
      serviceContribution: 0,
      gracePeriodMonths: 0,
      firstDraw: 100,
      fxAsOf: ref("2022-12-22"),
      closingDate: ref("2023-03-31"),
      firstPaymentDate: ref("2023-04-25"),
      opFeeFacility: 0,
      vatOpFeeFacility: 0,
      firstMonthsRent: 0,
      firstMonthVATOpFee: 0,
      partialDayRent: 0,
      partialDayVAT: 0,
    });

    const stateCotizacion = ref({
      plazo: 12,
      tipo: "Normal",
      subsidioVendor_12: 10,
      subsidioVendor_24: 10,
      subsidioVendor_36: 10,
      subsidioVendor_48: 10,
      subsidioVendor_60: 10,
      anticipoCapital_12: 10,
      anticipoCapital_24: 10,
      anticipoCapital_36: 10,
      anticipoCapital_48: 10,
      anticipoCapital_60: 10,
      tasa_12: 10,
      tasa_24: 10,
      tasa_36: 10,
      tasa_48: 10,
      tasa_60: 10,
      comision_12: 0,
      comision_24: 0,
      comision_36: 0,
      comision_48: 0,
      comision_60: 0,
      comisionPorc_12: 0,
      comisionPorc_24: 0,
      comisionPorc_36: 0,
      comisionPorc_48: 0,
      comisionPorc_60: 0,
      ultimaRenta_12: 0,
      ultimaRenta_24: 0,
      ultimaRenta_36: 0,
      ultimaRenta_48: 0,
      ultimaRenta_60: 0,
      residualCalculoPorc_12: 45,
      residualCalculoPorc_24: 45,
      residualCalculoPorc_36: 45,
      residualCalculoPorc_48: 45,
      residualCalculoPorc_60: 45,
      residualCalculo_12: 0,
      residualCalculo_24: 0,
      residualCalculo_36: 0,
      residualCalculo_48: 0,
      residualCalculo_60: 0,
      buyOut_12: 0,
      buyOut_24: 0,
      buyOut_36: 0,
      buyOut_48: 0,
      buyOut_60: 0,
      residualCliente_12: 45,
      residualCliente_24: 45,
      residualCliente_36: 45,
      residualCliente_48: 45,
      residualCliente_60: 45,
      opCalculo_12: 0,
      opCalculo_24: 0,
      opCalculo_36: 0,
      opCalculo_48: 0,
      opCalculo_60: 0,
      ocCliente_12: 0,
      ocCliente_24: 0,
      ocCliente_36: 0,
      ocCliente_48: 0,
      ocCliente_60: 0,
      tirObjetivo_12: 0,
      tirObjetivo_24: 0,
      tirObjetivo_36: 0,
      tirObjetivo_48: 0,
      tirObjetivo_60: 0,
      enganche_12: 0,
      enganche_24: 0,
      enganche_36: 0,
      enganche_48: 0,
      enganche_60: 0,
      montoFinanciar_12: 0,
      montoFinanciar_24: 0,
      montoFinanciar_36: 0,
      montoFinanciar_48: 0,
      montoFinanciar_60: 0,
      rentaMensual_12: 0,
      rentaMensual_24: 0,
      rentaMensual_36: 0,
      rentaMensual_48: 0,
      rentaMensual_60: 0,
      relacionRentaEquipo_12: 0,
      relacionRentaEquipo_24: 0,
      relacionRentaEquipo_36: 0,
      relacionRentaEquipo_48: 0,
      relacionRentaEquipo_60: 0,
      subtotal_12: 0,
      subtotal_24: 0,
      subtotal_36: 0,
      subtotal_48: 0,
      subtotal_60: 0,
      iva_12: 0,
      iva_24: 0,
      iva_36: 0,
      iva_48: 0,
      iva_60: 0,
      rentaDeposito_12: 1,
      rentaDeposito_24: 1,
      rentaDeposito_36: 1,
      rentaDeposito_48: 1,
      rentaDeposito_60: 1,
      totalPagoInicial_12: 0,
      totalPagoInicial_24: 0,
      totalPagoInicial_36: 0,
      totalPagoInicial_48: 0,
      totalPagoInicial_60: 0,
      ivaRenta_12: 0,
      ivaRenta_24: 0,
      ivaRenta_36: 0,
      ivaRenta_48: 0,
      ivaRenta_60: 0,
      totalRenta_12: 0,
      totalRenta_24: 0,
      totalRenta_36: 0,
      totalRenta_48: 0,
      totalRenta_60: 0,
      numRentaDeposito_12: 1,
      numRentaDeposito_24: 1,
      numRentaDeposito_36: 1,
      numRentaDeposito_48: 1,
      numRentaDeposito_60: 1,
      seguro: 0,
      gastosLegales: 0,
      gastosImportacion: 0,
    });

    /* const clienteSeleccionado = computed(() => {
      if (tablaTIR.value.length == 0 || !state.value.cliente_id) return undefined;

      return clientes.value.filter((cliente) => {
        if (cliente.id == state.value.cliente_id.id) {
          return cliente;
        }
      });
    }); */

    return {
      clientes,
      //clienteSeleccionado,
      state,
      stateCalculosLeaseParameters,
      stateCotizacion,
      tablaTIR,
      usuarioLoggeado,
    };
  }
);
